import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { DragDropContext } from "@hello-pangea/dnd";
import OrdersColumn from "./OrdersColumn";
import Pusher from "pusher-js";
import { adminApiRequest } from "../utils/adminApi";
import PageLoader from "./PageLoader";

const ORDER_STATUSES = [
  { id: "todo", label: "order received" },
  { id: "inprogress", label: "processing" },
  { id: "completed", label: "completed" },
];

const STATUS_MAP = {
  "to do": "todo",
  todo: "todo",
  "in progress": "inprogress",
  processing: "inprogress",
  completed: "completed",
};

const DB_STATUS_MAP = {
  todo: "to do",
  inprogress: "in progress",
  completed: "completed",
};

const toUkDbString = (date = new Date()) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/London",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const map = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`;
};

export default function OrdersTab({ role }) {
  const [columns, setColumns] = useState({
    todo: [],
    inprogress: [],
    completed: [],
  });
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragLoading, setDragLoading] = useState(false);
  const [pusherError, setPusherError] = useState(false);

  const pollingInterval = useRef(null);

  const sounds = useRef({
    todo: new Audio("/sounds/notification.mp3"),
    inprogress: new Audio("/sounds/inprogress-order.mp3"),
    completed: new Audio("/sounds/completed-order.mp3"),
  });

  const sortOrders = useCallback((orders, columnId) => {
    if (columnId === "completed") {
      return [...orders].sort(
        (a, b) => new Date(b.completed_at) - new Date(a.completed_at),
      );
    }
    return [...orders].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at),
    );
  }, []);

  const normalizeColumns = useCallback((orders) => {
    const col = { todo: [], inprogress: [], completed: [] };

    (orders || []).forEach((o) => {
      const key =
        STATUS_MAP[
          String(o?.status || "")
            .trim()
            .toLowerCase()
        ];
      if (key) col[key].push(o);
    });

    return col;
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const data = await adminApiRequest({
        script: "get-orders",
        token: localStorage.getItem("admin_token"),
      });

      const newColumns = normalizeColumns(data?.orders || []);
      setColumns(newColumns);
    } catch (err) {
      console.error("Polling fetch failed:", err);
    }
  }, [normalizeColumns]);

  const fetchSingleOrder = useCallback(async (orderId) => {
    const data = await adminApiRequest({
      script: "get-order",
      data: { order_id: orderId },
      token: localStorage.getItem("admin_token"),
    });

    const order = data?.order || data?.orders?.[0];
    if (!order?.id) {
      throw new Error("Invalid order response from get-order");
    }

    return order;
  }, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      setLoading(true);
      await fetchOrders();
      setLoading(false);
    };

    init();

    if (!process.env.REACT_APP_PUSHER_KEY) {
      console.error("Missing Pusher environment variables");
      return () => {};
    }

    const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY, {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
      forceTLS: true,
    });

    const channel = pusher.subscribe("orders-channel");

    channel.bind("new-order", async (data) => {
      if (!isMounted || !data?.order_id) return;

      try {
        const fullOrder = await fetchSingleOrder(data.order_id);

        if (!fullOrder?.id) {
          fetchOrders();
          return;
        }

        setColumns((prev) => {
          const exists = Object.values(prev)
            .flat()
            .some((o) => o?.id === fullOrder.id);

          if (exists) return prev;

          const updatedTodo = sortOrders([...prev.todo, fullOrder], "todo");

          sounds.current.todo.play().catch(() => {});

          return { ...prev, todo: updatedTodo };
        });
      } catch (err) {
        fetchOrders();
      }
    });

    pusher.connection.bind("state_change", (states) => {
      if (states.current === "disconnected" || states.current === "failed") {
        setPusherError(true);
        if (!pollingInterval.current) {
          pollingInterval.current = setInterval(fetchOrders, 5000);
        }
      }

      if (states.current === "connected") {
        setPusherError(false);
        if (pollingInterval.current) {
          clearInterval(pollingInterval.current);
          pollingInterval.current = null;
        }
      }
    });

    return () => {
      isMounted = false;
      channel.unbind_all();
      channel.unsubscribe();
      pusher.disconnect();

      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
        pollingInterval.current = null;
      }
    };
  }, [fetchOrders, fetchSingleOrder, sortOrders]);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const handleDragEnd = async (result) => {
    if (role === "staff") return;
    const { destination, draggableId, source } = result;
    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const orderId = parseInt(draggableId, 10);
    setDragLoading(true);
    const startTime = Date.now();

    const destKey = destination.droppableId;
    const newDbStatus = DB_STATUS_MAP[destKey];
    const nowUk = toUkDbString(new Date());

    setColumns((prev) => {
      const newColumns = { ...prev };

      const sourceList = Array.from(newColumns[source.droppableId] || []);
      const [movedOrderRaw] = sourceList.splice(source.index, 1);
      if (!movedOrderRaw) return prev;

      const movedOrder = {
        ...movedOrderRaw,
        status: newDbStatus,
        moved_at: nowUk,
        ...(destKey === "completed" ? { completed_at: nowUk } : {}),
      };

      const destList = Array.from(newColumns[destKey] || []);
      destList.push(movedOrder);

      newColumns[source.droppableId] = sourceList;
      newColumns[destKey] = sortOrders(destList, destKey);

      return newColumns;
    });

    sounds.current?.[destKey]?.play?.().catch(() => {});

    try {
      await adminApiRequest({
        script: "update-order-status",
        data: {
          order_id: orderId,
          status: newDbStatus,
        },
        token: localStorage.getItem("admin_token"),
      });
      // Do NOT refetch here — the optimistic update already has the correct
      // completed_at timestamp. Refetching immediately risks getting stale
      // server data before the DB write has committed, which wipes completed_at.
    } catch (err) {
      // Only refetch on error to restore correct server state
      await fetchOrders();
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = 1500 - elapsed;
      setTimeout(() => setDragLoading(false), remaining > 0 ? remaining : 0);
    }
  };

  const totalCompletedPrice = useMemo(() => {
    return (columns.completed || []).reduce(
      (sum, order) => sum + (Number(order?.total_price) || 0),
      0,
    );
  }, [columns.completed]);

  if (loading) {
    return <PageLoader message="Fetching Orders..." />;
  }

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {dragLoading && (
        <PageLoader 
          fullPage 
          message="Updating Status..." 
        />
      )}

      {pusherError && (
        <div className="mb-5 shrink-0 rounded-2xl border border-red-200 bg-red-500 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm">
          ⚠️ Real-time updates unavailable. Falling back to refresh mode.
        </div>
      )}

      <div className="min-h-0 flex-1">
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex h-full min-h-0 gap-4 overflow-x-auto pb-2 xl:overflow-x-hidden">
            {ORDER_STATUSES.map(({ id, label }) => (
              <OrdersColumn
                key={id}
                droppableId={id}
                statusLabel={label}
                orders={columns[id]}
                expandedOrderId={expandedOrderId}
                role={role}
                toggleOrder={toggleOrder}
                refetchOrders={fetchOrders}
                footer={
                  id === "completed" && (columns.completed || []).length > 0 ? (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-md font-bold text-emerald-700">
                      Total Completed Orders: £{totalCompletedPrice.toFixed(2)}
                    </div>
                  ) : null
                }
              />
            ))}
          </div>
        </DragDropContext>
      </div>
    </div>
  );
}
